import { useState } from 'react'
import type { GrowthCategory, GrowthContent } from '@mc/api'
import {
  Button,
  Dropdown,
  DropdownToggle,
  IconArrowLeftDefault,
  IconChevronRightDefault,
  SpinningLoader,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  type Subcat,
  preloadCategoryImages,
  useCategories,
  useCoursesBySubcategory,
  useSubcategoriesForCategory,
} from '../../dataFetching'
import { trackMainCategoryClicked, trackSubCategoryClicked } from '../../events'
import { CourseButton } from '../CourseButton'
import { NavDropdownContent } from '../DropdownContent'
import { GreaterThanOrEqual, LessThan } from '../Media'
import { MobileDropdown } from '../MobileDropdown'
import { NavCta } from '../NavCta'
import { ViewAllCatsCta } from './ViewAllCatsCta'
import classes from '../Browse/Browse.module.scss'

export const SubcategoryBrowsingDropdownContent = () => {
  const { categories, isLoading } = useCategories()

  return (
    <>
      <div
        className={`${classes.browseDropdownContent} d-flex flex-column mc-m-3`}
      >
        {isLoading ? (
          <LoaderDropdown />
        ) : (
          categories.map((category, idx) => (
            <CategoryButton
              key={category.slug}
              category={category}
              order={idx}
            />
          ))
        )}
      </div>
      {!isLoading && <ViewAllCatsCta />}
    </>
  )
}

export const btnClasses = (show: boolean) =>
  `mc-px-3 mc-py-3 d-flex ${classes.categoryBtn} ${show ? classes.active : ''}`

const CategoryButton = ({
  category,
  order,
}: {
  category: GrowthCategory
  order: number
}) => {
  const [show, setShow] = useState(false)
  const breakpoint = 'lg'

  const categoryBtn = (
    <div className='d-flex'>
      <Button
        kind='tertiary'
        fullWidth
        className={btnClasses(show)}
        onClick={() => {
          trackMainCategoryClicked({
            id: String(category.id),
            categoryName: category.name,
            order,
          })
          setShow((prev) => !prev)
        }}
        onMouseEnter={(e: React.MouseEvent) =>
          categoryHoverSmartPreload(e, category.id)
        }
      >
        <span className='flex-grow-1'>{category.name}</span>
        <IconChevronRightDefault size='md' />
      </Button>
    </div>
  )

  return (
    <>
      <LessThan bp={breakpoint}>
        {categoryBtn}
        {show && (
          <MobileDropdown>
            <div className='mc-px-3 mc-mt-3'>
              <Button
                rounded
                kind='secondary'
                className='mc-mx-2'
                onClick={() => setShow(false)}
              >
                <IconArrowLeftDefault />
              </Button>
            </div>
            <SubcategoriesDropdownContent
              show={show}
              setShow={setShow}
              category={category}
            />
          </MobileDropdown>
        )}
      </LessThan>
      <GreaterThanOrEqual bp={breakpoint}>
        <Dropdown placement='right'>
          <DropdownToggle>{categoryBtn}</DropdownToggle>
          {show ? (
            <SubcategoriesDropdownContent
              show={show}
              setShow={setShow}
              category={category}
            />
          ) : (
            <></>
          )}
        </Dropdown>
      </GreaterThanOrEqual>
    </>
  )
}

export const SubcategoriesDropdownContent = ({
  show,
  setShow,
  category,
}: {
  show: boolean
  setShow(s: boolean): void
  category: GrowthCategory
}) => {
  const categorySlug = category.slug
  const catSubcats = useSubcategoriesForCategory({ categorySlug })

  if (!catSubcats) {
    return (
      <NavDropdownContent show={show} setShow={setShow}>
        <LoaderDropdown />
      </NavDropdownContent>
    )
  }

  return (
    <NavDropdownContent show={show} setShow={setShow}>
      <div className='mc-p-3'>
        <div>
          {catSubcats.subcategories.map((sc) => (
            <SubcategoryButton
              key={`${sc.id}-${sc.name}`}
              subcat={sc}
              category={category}
            />
          ))}
        </div>
      </div>
    </NavDropdownContent>
  )
}

const SubcategoryButton = ({
  category,
  subcat,
}: {
  category: GrowthCategory
  subcat: Subcat
}) => {
  const categorySlug = category.slug
  const [show, setShow] = useState(false)
  const { subcatCourses } = useCoursesBySubcategory(categorySlug, subcat)

  const breakpoint = 'lg'

  const button = (
    <Button
      kind='tertiary'
      fullWidth
      className={btnClasses(show)}
      onClick={() => {
        trackSubCategoryClicked({
          catName: category.name,
          subcatName: subcat.name,
          subcatId: subcat.id,
        })
        setShow((prev) => !prev)
      }}
    >
      <span className='flex-grow-1'>{subcat.name}</span>
      <IconChevronRightDefault size='md' />
    </Button>
  )

  const dropdownContent = (
    <SubcategoryCoursesDropdownContent
      show={show}
      setShow={setShow}
      category={category}
      subcat={subcat}
      courseData={subcatCourses}
    />
  )

  return (
    <>
      <LessThan bp={breakpoint}>
        {button}
        {show && (
          <MobileDropdown>
            <div className='mc-px-3 mc-mt-3 mc-mb-1'>
              <Button
                rounded
                kind='secondary'
                className='mc-mx-2'
                onClick={() => setShow(false)}
              >
                <IconArrowLeftDefault />
              </Button>
            </div>
            {dropdownContent}
          </MobileDropdown>
        )}
      </LessThan>
      <GreaterThanOrEqual bp={breakpoint}>
        <Dropdown placement='right-start'>
          <DropdownToggle>{button}</DropdownToggle>
          {show ? dropdownContent : <></>}
        </Dropdown>
      </GreaterThanOrEqual>
    </>
  )
}

/**
 * The setTimeout here prevents all categories from getting preloaded after the mouse rolls past the buttons.
 * The category preload is only triggered if the button is hovered over at trigger and is still hovered over after 200ms.
 */
export const categoryHoverSmartPreload = (
  e: React.MouseEvent,
  categoryId: GrowthCategory['id'],
) => {
  const btn = e.target as HTMLButtonElement
  setTimeout(() => {
    if (btn?.matches?.(':hover')) {
      preloadCategoryImages({ categoryId })
    }
  }, 200)
}

const LoaderDropdown = () => (
  <div className={`${classes.browseDropdownContent} d-flex flex-column`}>
    <div className={classes.spinnerContainer}>
      <SpinningLoader />
    </div>
  </div>
)

const SubcategoryCoursesDropdownContent = ({
  show,
  setShow,
  category,
  subcat,
  courseData,
}: {
  show: boolean
  setShow(s: boolean): void
  category: GrowthCategory
  subcat: Subcat
  courseData: GrowthContent[] | null
}) => {
  const { t } = useTranslation('@mc/next')

  if (!courseData) {
    return (
      <NavDropdownContent show={show} setShow={setShow}>
        <LoaderDropdown />
      </NavDropdownContent>
    )
  }
  if (courseData.length === 0) {
    return (
      <NavDropdownContent show={show} setShow={setShow}>
        <div className='mc-opacity--muted mc-px-3 mc-py-5 mc-text--center'>
          {t('nav.noContent')}
        </div>
      </NavDropdownContent>
    )
  }

  return (
    <NavDropdownContent show={show} setShow={setShow}>
      <div
        className={`mc-p-3 d-flex flex-column ${classes.browseDropdownContent}`}
      >
        {courseData.map((course) => (
          <CourseButton
            key={course.slug}
            course={course}
            category={category}
            subcat={subcat}
          />
        ))}
      </div>
      <div className='mc-mx-3 mc-mb-3'>
        <NavCta category={category} subcat={subcat} />
      </div>
    </NavDropdownContent>
  )
}
